import type { ComponentType } from 'react';
import React, { useContext, useEffect, useState } from 'react';

import { getMeta } from '@swf/components/StarsWizard/Machine';
import { Context } from '@swf/components/StarsWizard/StarsWizard';
import { getDisplayName } from '@swf/core/utils';

export enum Status {
  Idle,
  Loading,
  Success,
  Failure
}

export interface InjectedWizardLoadingProps {
  status: Status;
}

const withWizardLoading = (Component: React.ComponentType<InjectedWizardLoadingProps>) => {
  const WithWizardLoading: React.FunctionComponent = (props) => {
    const wizard = useContext(Context);
    const [status, setStatus] = useState(Status.Idle);

    useEffect(() => {
      if (!wizard) {
        return undefined;
      }

      const transitionHandler = ({
        meta,
        context: { error }
      }: {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        meta: any;
        context: { error: any };
        /* eslint-enable @typescript-eslint/no-explicit-any */
      }) => {
        const { waiting } = getMeta(meta) as { waiting?: boolean };

        if (waiting) {
          setStatus(Status.Loading);
        } else if (status === Status.Loading) {
          setStatus(error ? Status.Failure : Status.Success);
        }
      };

      wizard.onTransition(transitionHandler);

      return () => {
        wizard.off(transitionHandler);
      };
    }, [status, wizard]);

    return React.createElement(Component, {
      ...props,
      status
    });
  };

  WithWizardLoading.displayName = `WithWizardLoading(${getDisplayName(
    Component as ComponentType
  )})`;

  return WithWizardLoading;
};

export default withWizardLoading;
